import { DEFAULT_CURRENCY_CODE, Inject, Injectable } from '@angular/core';
import PaymentTerminal from '../../plugins/payment-terminal';
import { PaymentMethodType } from './models/payment.models';
import { Settings } from './models/models';
import { TerminalError } from '../payment/terminal-error';
import { DataService } from './data.service';
import { APP_SETTINGS } from './settings-data.provider';
import { environment } from '../../environments/environment';
import PaymentRemoteTerminal from '../../plugins/payment-remote-terminal';

enum TerminalErrorCodes {
  CANCELLED_WHILE_WAITING_FOR_CARD = '172D',
  CANCELLED_BY_CASHIER_WHILE_WAITING_FOR_CARD = '1785'
}

@Injectable({ providedIn: 'root' })
export class PaymentService {
  constructor(private readonly dataService: DataService,
    @Inject(APP_SETTINGS) private readonly settings: Settings,
    @Inject(DEFAULT_CURRENCY_CODE) private readonly currency: string) {
  }

  async callTerminalPayment(): Promise<boolean> {
    const paymentMethod = this.settings.paymentMethods.find(({ gateway }) => gateway === 'versa_commerce_pos_external');

    if (!paymentMethod) {
      throw new Error('No external payment defined in current settings');
    }

    const { hasConfiguredTerminal } = await PaymentTerminal.checkHasConfiguredTerminal();
    if (!hasConfiguredTerminal) {
      throw new Error('No CCV terminal available!');
    }

    try {
      await PaymentTerminal.makePayment({
        amount: this.dataService.cart.totalPrice.toString(),
        currency: this.currency
      });
      await this.dataService.checkoutCart({
        method: PaymentMethodType.creditCard,
        cardType: 'cc', // FIXME: ???
        paymentMethod,
      });
      return true;
    } catch (error: any) {
      //Special case for user cancellation
      if (error.data.errorCode === TerminalErrorCodes.CANCELLED_WHILE_WAITING_FOR_CARD) {
        return false;
      }
      throw new TerminalError(error.data);
    }
  }

  async callRemoteTerminalPayment(ipAddress: string): Promise<boolean> {
    const paymentMethod = this.settings.paymentMethods.find(({ gateway }) => gateway === 'versa_commerce_pos_opi');

    if (!paymentMethod) {
      throw new Error('No external payment defined in current settings');
    }

    try {
      const result = await PaymentRemoteTerminal.makePayment({
        amount: this.dataService.cart.totalPrice.toString(),
        currency: this.currency,
        terminal: {
          ipAddress: ipAddress,
          port: 20002,
          compatibilityPort: 20007,
          socketMode: 'SINGLE_SOCKET',
          terminalType: 'ATTENDED_OPI_DE'
        }
      });
      await this.dataService.updateCartAttributes({
        attributes: {
          opi_payment_transaction_id: result.transactionId
        }
      });
      await this.dataService.checkoutCart({
        method: PaymentMethodType.creditCard,
        cardType: 'cc', // FIXME: ???
        paymentMethod,
      });
      return true;
    } catch (error: any) {
      //Special case for user cancellation
      if (error.data.errorCode === TerminalErrorCodes.CANCELLED_WHILE_WAITING_FOR_CARD
        || error.data.errorCode === TerminalErrorCodes.CANCELLED_BY_CASHIER_WHILE_WAITING_FOR_CARD) {
        return false;
      }
      throw new TerminalError(error.data);
    }
  }

  async callRemoteTerminalReversePayment(transactionId: string, ipAddress: string) {
    try {
      await PaymentRemoteTerminal.revertPayment({
        transactionId,
        terminal: {
          ipAddress: ipAddress,
          port: 20002,
          compatibilityPort: 20007,
          socketMode: 'SINGLE_SOCKET',
          terminalType: 'ATTENDED_OPI_DE'
        }
      });
      return;
    } catch (error: any) {
      throw new TerminalError(error.data);
    }
  }

  async callRemoteTerminalRefundPayment(transactionId: string, ipAddress: string, amount: number, currency: string) {
    try {
      const chargedAmount = this.getChargedAmount(amount);
      await PaymentRemoteTerminal.refundPayment({
        transactionId,
        amount: chargedAmount,
        currency,
        terminal: {
          ipAddress: ipAddress,
          port: 20002,
          compatibilityPort: 20007,
          socketMode: 'SINGLE_SOCKET',
          terminalType: 'ATTENDED_OPI_DE'
        }
      });
      return;
    } catch (error: any) {
      throw new TerminalError(error.data);
    }
  }

  private getChargedAmount(amount: number): string {
    let absoluteAmount = Math.abs(amount);
    return absoluteAmount.toString();
  }

  async callCancelRemoteTerminalPayment() {
    const paymentMethod = this.settings.paymentMethods.find(({ gateway }) => gateway === 'versa_commerce_pos_opi');

    if (!paymentMethod) {
      throw new Error('No external payment defined in current settings');
    }

    try {
      await PaymentRemoteTerminal.cancelPayment({
        terminal: {
          ipAddress: paymentMethod.settings?.terminalIp!,
          port: 20002,
          compatibilityPort: 20007,
          socketMode: 'SINGLE_SOCKET',
          terminalType: 'ATTENDED_OPI_DE'
        }
      });
      return true;
    } catch (error: any) {
      throw new TerminalError(error.data);
    }

  }

  async callPeriodCloseRemoteTerminalPayment() {
    const paymentMethod = this.settings.paymentMethods.find(({ gateway }) => gateway === 'versa_commerce_pos_opi');

    if (!paymentMethod || environment.runOnAndroidPaymentTerminal) {
      return;
    }

    try {
      return await PaymentRemoteTerminal.closePeriod({
        terminal: {
          ipAddress: paymentMethod.settings?.terminalIp!,
          port: 20002,
          compatibilityPort: 20007,
          socketMode: 'SINGLE_SOCKET',
          terminalType: 'ATTENDED_OPI_DE'
        }
      });
    } catch (error: any) {
      throw new TerminalError(error.data);
    }
  }

  async callPeriodCloseLocalTerminalPayment() {

    if (!environment.runOnAndroidPaymentTerminal) {
      return;
    }

    try {
      return await PaymentTerminal.closePeriod();
    } catch (error: any) {
      throw new TerminalError(error.data);
    }
  }

  async checkTerminalAvailable(ipAddress: string) {
    try {
      if (!ipAddress) {
        return false;
      }
      const response = await PaymentRemoteTerminal.getTerminalStatus({
        terminal: {
          ipAddress: ipAddress,
          port: 20002,
          compatibilityPort: 20007,
          socketMode: 'SINGLE_SOCKET',
          terminalType: 'ATTENDED_OPI_DE'
        }
      });
      if (response) {
        return true;
      }
    } catch (error: any) {
      return false;
    }
    return false;
  }
}
